// 매장 지도 공통 로직 (지도·핀·묶음). 화면 안에 있는 핀만 그리고,
// 멀리서 보면 가까이 붙은 핀을 숫자 동그라미로 묶는다.
// 지금은 매장찾기(StoreFinder) 한 곳에서 쓰지만, 화면 로직과 지도 로직을 나눠 두려고 파일을 분리했다.
//
// 쓰는 쪽에서 준비해야 하는 것
// - <div ref="mapZone">      : 지도가 들어갈 자리
// - storeRows                : 지도에 그릴 매장 목록
// - focusedId                : 지금 고른 매장 id
// - hasLocation / nowLat / nowLng : 내 위치
// - onMarkerClick(item)      : 핀을 눌렀을 때 할 일
// - bottomReserve()          : (선택) 지도 아래쪽에서 비워둘 높이. 기본은 네이버 로고 자리만
import {
  CLUSTER_CELL_PX,
  MAP_BOTTOM_RESERVE,
  MAP_ZOOM,
  MARKER_BLOCK,
  MY_LOCATION_ANCHOR,
  clusterIcon,
  isMobileView,
  myLocationHtml,
  storeMarkerIcon,
} from "@/utils/storeFinder";

export default {
  data() {
    return {
      mapZoom: MAP_ZOOM.initial,
      labelMinZoom: MAP_ZOOM.labelMin,
    };
  },
  created() {
    // 지도 객체는 Vue가 추적하지 않도록 data 밖에 둔다
    this.map = null;
    this.markerById = {};
    this.rowById = {};
    this.clusterMarkers = [];
    this.myLocationMarker = null;
    this.resizeObserver = null;
    // 지도 크기가 바뀌면 다시 가운데 맞출 대상 { lat, lng, type }
    this.centerTarget = null;
    // 묶음을 눌러 펼친 매장들 (핀만 파란색으로)
    this.highlightIds = new Set();
  },
  beforeDestroy() {
    this.destroyMap();
  },
  methods: {
    naverMaps() {
      return window.naver && window.naver.maps ? window.naver.maps : null;
    },

    // 지도는 한 번만 만든다. 손 조작은 매장찾기·신청서가 같다
    // (한 손가락 끌기·두 손가락 확대·두 번 톡 확대 허용, PC 휠은 페이지 스크롤과 부딪쳐서 끔)
    createMap(center, zoom = MAP_ZOOM.initial) {
      const nm = this.naverMaps();
      const zone = this.$refs.mapZone;
      if (!nm || !zone) {
        console.error("Naver Maps API not loaded");
        return null;
      }
      this.map = new nm.Map(zone, {
        center: new nm.LatLng(center.lat, center.lng),
        zoom,
        minZoom: MAP_ZOOM.min,
        maxZoom: MAP_ZOOM.max,
        draggable: true,
        pinchZoom: true,
        disableDoubleTapZoom: false,
        disableDoubleClickZoom: false,
        scrollWheel: false,
        keyboardShortcuts: false,
        zoomControl: false,
      });
      this.mapZoom = this.map.getZoom();
      nm.Event.addListener(this.map, "zoom_changed", () => {
        this.mapZoom = this.map.getZoom();
      });
      // 끌기·확대가 끝났을 때만 묶음·보이는 핀을 다시 계산 (움직이는 중에는 계산하지 않음)
      nm.Event.addListener(this.map, "idle", () => this.updateMarkerVisibility());
      // 지도 칸 크기가 나중에 바뀌어도(헤더 높이 계산, 펼침 등) 지도가 따라가게 한다
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.onMapResize());
        this.resizeObserver.observe(zone);
      }
      return this.map;
    },

    destroyMap() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      this.clearClusters();
      Object.values(this.markerById).forEach((m) => m.setMap(null));
      this.markerById = {};
      this.rowById = {};
      if (this.myLocationMarker) {
        this.myLocationMarker.setMap(null);
        this.myLocationMarker = null;
      }
      if (this.map && typeof this.map.destroy === "function") {
        try {
          this.map.destroy();
        } catch (e) {
          console.warn("naver map destroy failed:", e);
        }
      }
      this.map = null;
    },

    onMapResize() {
      const nm = this.naverMaps();
      const zone = this.$refs.mapZone;
      if (!nm || !this.map || !zone) return;
      const size = this.map.getSize();
      if (size.width === zone.clientWidth && size.height === zone.clientHeight) return;
      this.map.setSize(new nm.Size(zone.clientWidth, zone.clientHeight));
      if (this.centerTarget) {
        const t = this.centerTarget;
        this.centerVisually(t.lat, t.lng, t.type);
      }
    },

    // 지도 아래쪽에서 가운데 계산에서 빼는 높이 (기본: 네이버 로고·저작권 표시 자리)
    bottomReserve() {
      return MAP_BOTTOM_RESERVE[isMobileView() ? "mobile" : "desktop"].normal;
    },

    // 마커(라벨+핀 전체)가 지도에서 눈에 보이는 가운데에 오도록 지도 중심을 맞춘다
    // type: "store"(핀 끝 기준) | "myLocation"(파란 점 기준)
    centerVisually(lat, lng, type) {
      const nm = this.naverMaps();
      if (!nm || !this.map) return;
      this.centerTarget = { lat, lng, type };
      const block = MARKER_BLOCK[type][isMobileView() ? "mobile" : "desktop"];
      const size = this.map.getSize();
      const freeCenterY = (size.height - this.bottomReserve()) / 2;
      const anchorY = freeCenterY + (block.above - block.below) / 2;
      const shift = anchorY - size.height / 2;
      const proj = this.map.getProjection();
      const p = proj.fromCoordToOffset(new nm.LatLng(lat, lng));
      this.map.setCenter(proj.fromOffsetToCoord(new nm.Point(p.x, p.y - shift)));
    },

    // +/− 버튼 (두 손가락 확대가 어려운 분용)
    zoomBy(delta) {
      if (!this.map) return;
      const next = Math.min(MAP_ZOOM.max, Math.max(MAP_ZOOM.min, this.map.getZoom() + delta));
      this.map.setZoom(next, true);
    },

    renderMarkers() {
      const nm = this.naverMaps();
      if (!nm || !this.map) return;
      Object.values(this.markerById).forEach((m) => m.setMap(null));
      this.markerById = {};
      this.rowById = {};
      this.storeRows.forEach((item) => {
        this.rowById[item.id] = item;
        const marker = new nm.Marker({
          position: new nm.LatLng(item.lat, item.lng),
          map: null, // 보일지 말지는 updateMarkerVisibility에서 정한다
          clickable: true,
          icon: storeMarkerIcon(nm, item.name, item.id === this.focusedId, this.highlightIds.has(item.id)),
          zIndex: this.markerZIndex(item.id),
        });
        marker.addListener("click", () => this.onMarkerClick(item));
        this.markerById[item.id] = marker;
      });
      this.updateMarkerVisibility();
    },

    clearClusters() {
      (this.clusterMarkers || []).forEach((m) => m.setMap(null));
      this.clusterMarkers = [];
    },

    // 화면 안(+여유 한 칸)의 핀만 그리고, 멀리서 볼 때는 가까이 붙은 핀을 숫자 동그라미로 묶는다
    // (핀 142개를 늘 그리지 않아 가볍고, 축소해도 핀이 겹쳐 보이지 않음)
    updateMarkerVisibility() {
      const nm = this.naverMaps();
      if (!nm || !this.map) return;
      const proj = this.map.getProjection();
      const size = this.map.getSize();
      const c = proj.fromCoordToOffset(this.map.getCenter());
      const pad = CLUSTER_CELL_PX;
      const left = c.x - size.width / 2 - pad;
      const right = c.x + size.width / 2 + pad;
      const top = c.y - size.height / 2 - pad;
      const bottom = c.y + size.height / 2 + pad;
      const clustering = this.map.getZoom() < MAP_ZOOM.labelMin;
      const show = (m, on) => {
        if (on && !m.getMap()) m.setMap(this.map);
        if (!on && m.getMap()) m.setMap(null);
      };

      const cells = {};
      this.storeRows.forEach((s) => {
        const marker = this.markerById[s.id];
        if (!marker) return;
        const p = proj.fromCoordToOffset(new nm.LatLng(s.lat, s.lng));
        const inView = p.x >= left && p.x <= right && p.y >= top && p.y <= bottom;
        if (!inView) return show(marker, false);
        // 지금 보고 있는 매장은 묶지 않고 항상 따로 보여준다
        if (!clustering || s.id === this.focusedId) return show(marker, true);
        const key = `${Math.floor(p.x / CLUSTER_CELL_PX)}:${Math.floor(p.y / CLUSTER_CELL_PX)}`;
        (cells[key] = cells[key] || []).push(s);
      });

      this.clearClusters();
      Object.values(cells).forEach((group) => {
        if (group.length === 1) {
          show(this.markerById[group[0].id], true);
          return;
        }
        group.forEach((s) => show(this.markerById[s.id], false));
        const lat = group.reduce((sum, s) => sum + s.lat, 0) / group.length;
        const lng = group.reduce((sum, s) => sum + s.lng, 0) / group.length;
        const cluster = new nm.Marker({
          position: new nm.LatLng(lat, lng),
          map: this.map,
          clickable: true,
          zIndex: 50,
          icon: clusterIcon(nm, group.length),
        });
        cluster.addListener("click", () => this.zoomIntoCluster(group));
        this.clusterMarkers.push(cluster);
      });
    },

    // 묶음을 누르면 그 매장 핀만 파란색으로 표시하고, 핀이 잘리거나 버튼에 가리지 않게 한 화면에 들어오도록 확대
    zoomIntoCluster(group) {
      const nm = this.naverMaps();
      if (!nm || !this.map) return;
      this.setHighlight(group.map((s) => s.id));
      const proj = this.map.getProjection();
      const size = this.map.getSize();
      const zoom = this.map.getZoom();
      const pts = group.map((s) => proj.fromCoordToOffset(new nm.LatLng(s.lat, s.lng)));
      const xs = pts.map((p) => p.x);
      const ys = pts.map((p) => p.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      // 확대 z에서 묶음 전체(현재 크기 × 2^단계)가 안전 영역에 들어가는지
      const fits = (z) => {
        const inset = this.pinSafeInset(z);
        const k = 2 ** (z - zoom);
        return (
          (maxX - minX) * k <= size.width - inset.left - inset.right &&
          (maxY - minY) * k <= size.height - inset.top - inset.bottom
        );
      };
      // 최소 한 단계는 확대하고, 다 들어가는 한 더 확대 (최대: 목록에서 고를 때와 같은 확대)
      let target = zoom + 1;
      while (target < MAP_ZOOM.initial && fits(target + 1)) target += 1;
      // 묶음 가운데가 안전 영역의 가운데에 오도록 지도 중심을 옮긴 뒤 확대
      const inset = this.pinSafeInset(target);
      const k = 2 ** (target - zoom);
      const center = new nm.Point(
        (minX + maxX) / 2 - (inset.left - inset.right) / 2 / k,
        (minY + maxY) / 2 - (inset.top - inset.bottom) / 2 / k
      );
      this.map.setCenter(proj.fromOffsetToCoord(center));
      this.map.setZoom(target);
    },

    // 핀 끝이 이 여백 안쪽에 있으면 핀(+라벨)이 잘리지 않고 로고·버튼에 가리지 않는다
    pinSafeInset(zoom) {
      const view = isMobileView() ? "mobile" : "desktop";
      const block = MARKER_BLOCK[zoom >= MAP_ZOOM.labelMin ? "store" : "storePin"][view];
      return {
        top: block.above + 8,
        bottom: this.bottomReserve(),
        left: 30, // 핀 절반 + 여유
        right: 60, // +/− 버튼
      };
    },

    // 내 위치를 눈에 보이는 가운데에 두고, 가장 가까운 매장까지 함께 보이도록 확대 정도를 맞춘다
    fitMyLocationAnd(item) {
      const nm = this.naverMaps();
      if (!nm || !this.map || !item) return;
      // 내 위치를 중심으로 매장의 반대편 점까지 넣은 범위 → 확대 정도만 이 범위에 맞춘다
      const dLat = item.lat - this.nowLat;
      const dLng = item.lng - this.nowLng;
      const a = new nm.LatLng(this.nowLat - Math.abs(dLat), this.nowLng - Math.abs(dLng));
      const b = new nm.LatLng(this.nowLat + Math.abs(dLat), this.nowLng + Math.abs(dLng));
      this.map.fitBounds(new nm.LatLngBounds(a, b), this.fitMargin());
      if (this.map.getZoom() > MAP_ZOOM.myLocationMax) this.map.setZoom(MAP_ZOOM.myLocationMax);
      this.centerVisually(this.nowLat, this.nowLng, "myLocation");
    },

    // 범위 맞춤 여백: 지도 크기에 비례 (작은 지도에서 여백이 지도보다 커지지 않게)
    fitMargin() {
      const size = this.map.getSize();
      return {
        top: Math.round(size.height * 0.3),
        bottom: Math.round(size.height * 0.15),
        left: Math.round(size.width * 0.12),
        right: Math.round(size.width * 0.15),
      };
    },

    // 지금 보고 있는 매장 > 펼친 묶음 매장 > 나머지 순으로 위에 그린다
    markerZIndex(id) {
      if (id === this.focusedId) return 100;
      return this.highlightIds.has(id) ? 50 : 0;
    },

    // 파란 핀으로 표시할 매장을 바꾼다. 바뀐 매장의 아이콘만 다시 그린다 (전체를 다시 그리지 않음)
    setHighlight(ids) {
      const nm = this.naverMaps();
      const next = new Set(ids);
      const changed = new Set([...this.highlightIds, ...next]);
      this.highlightIds = next;
      if (!nm) return;
      changed.forEach((id) => {
        const marker = this.markerById[id];
        const row = this.rowById[id];
        if (!marker || !row) return;
        marker.setIcon(storeMarkerIcon(nm, row.name, id === this.focusedId, next.has(id)));
        marker.setZIndex(this.markerZIndex(id));
      });
    },

    clearHighlight() {
      if (this.highlightIds.size) this.setHighlight([]);
    },

    drawMyLocation() {
      const nm = this.naverMaps();
      if (!nm || !this.map || !this.hasLocation) return;
      const position = new nm.LatLng(this.nowLat, this.nowLng);
      if (this.myLocationMarker) {
        this.myLocationMarker.setPosition(position);
        return;
      }
      this.myLocationMarker = new nm.Marker({
        position,
        map: this.map,
        clickable: false,
        zIndex: 200,
        icon: {
          content: myLocationHtml(),
          size: new nm.Size(80, 44),
          anchor: new nm.Point(MY_LOCATION_ANCHOR.x, MY_LOCATION_ANCHOR.y),
        },
      });
    },
  },
};
